import {Reveal} from '@/components/motion/Reveal';
import {Sparkle} from '@/components/motion/Sparkle';
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from '@/components/ui/accordion';
import {useOfficialTerms} from '@/components/vendors/OfficialTermsDialog';
import {OFFICIAL_TERMS_TITLE} from '@/lib/official-terms';
import {BookOpenText} from 'lucide-react';

/**
 * Términos y condiciones del evento.
 *
 * Los items son el resumen editorial; el documento legal completo se abre en
 * la modal de reglas (OfficialTermsProvider) desde el último item. El texto
 * oficial no se duplica aquí.
 */
type Rule = {
  q: string;
  /** Párrafo de respuesta. */
  a: string;
  /** Si está, el pie de la respuesta abre la modal con el documento oficial. */
  terms?: {label: string};
};

const RULES: Rule[] = [
  {
    q: 'Selección y confirmación',
    a: 'Enviar la solicitud no garantiza un espacio. El equipo de Navimarket evalúa todas las solicitudes y notifica por correo electrónico a los negocios seleccionados.',
  },
  {
    q: 'Montaje y horarios',
    a: 'Los detalles de montaje, horarios de llegada y desmontaje se compartirán con los comercios participantes seleccionados.',
  },
  {
    q: 'Pagos con ATH Business',
    a: 'Los negocios participantes deben contar con ATH Business activo para procesar pagos durante el evento. Todo negocio debe contar con dos métodos de pago, siendo ATH Business uno de ellos.',
  },
  {
    q: 'Espacio y equipo',
    a: 'Cada comercio participante recibe un booth de 8×8. Mesa (~6 pies), mantel y silla están disponibles solo si se solicitan en el formulario de registro. Lo que no se solicite ahí no se proveerá más adelante: cada comercio participante será responsable de llevar el equipo que necesite y no haya pedido por el formulario.',
  },
  {
    q: OFFICIAL_TERMS_TITLE,
    a: 'Los Términos y Condiciones completos que rigen la participación de cada comerciante, se encuentran en el documento oficial de reglas para comercios Navimarket 2026',
    terms: {label: 'Leer reglas comercios Navimarket 2026'},
  },
];

export function Rules() {
  const openTerms = useOfficialTerms();

  return (
    <section aria-labelledby="reglas-title" className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6">
      <Sparkle className="absolute -right-16 top-14 hidden lg:block" size={16} delay={0.6} />
      <Reveal>
        <p className="overline-label text-amber">Reglas del evento</p>
        <h2 id="reglas-title" className="mt-3 text-3xl font-bold text-white">
          Lo que todo comercio participante debe saber
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="mt-8">
        <Accordion type="single" collapsible className="w-full">
          {RULES.map((r) => (
            <AccordionItem key={r.q} value={r.q} className="border-white/10">
              <AccordionTrigger className="py-5 text-left text-base font-medium text-white hover:text-amber hover:no-underline">
                {r.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-cream/85">
                <p>{r.a}</p>
                {r.terms && (
                  <p className="mt-4">
                    <button
                      type="button"
                      onClick={openTerms}
                      className="inline-flex items-center gap-1.5 rounded-sm font-medium text-amber underline underline-offset-4 transition-colors hover:text-tangerine focus-visible:outline-2 focus-visible:outline-offset-4"
                    >
                      {r.terms.label}
                      <BookOpenText className="size-4" aria-hidden />
                    </button>
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>
  );
}
